import React, { useEffect, useState } from "react";
import { Modal, Form, Select, Switch, Input, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { getUsers } from "../../services/users";
import { getSubscriptionPlans } from "../../services/subscriptionPlan";
import { IUser, ISubscriptionPlan } from "../../types/interfaces";

const { Dragger } = Upload;

interface AssignSubscriptionModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  confirmLoading: boolean;
}

const AssignSubscriptionModal: React.FC<AssignSubscriptionModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  confirmLoading,
}) => {
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [receiptFile, setReceiptFile] = useState<any>(null);

  // Filtrar solo usuarios tipo empresa usando query params directos
  const { data: usersData, isLoading: loadingUsers } = useQuery<{ data: IUser[], total: number }>({
    queryKey: ["users", "empresa", searchTerm],
    queryFn: () =>
      getUsers({
        page: 1,
        limit: 50,
        type: "empresa",
        name: searchTerm || undefined,
      }),
    enabled: visible,
  });

  const { data: plans, isLoading: loadingPlans } = useQuery<
    ISubscriptionPlan[]
  >({
    queryKey: ["subscriptionPlans", "active"],
    queryFn: getSubscriptionPlans,
    enabled: visible,
  });

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit({ ...values, receiptFile });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setReceiptFile(null);
      setSearchTerm("");
    } else {
      form.setFieldsValue({
        active: true,
      });
    }
  }, [visible, form]);

  const activePlans =
    plans?.filter((plan) => String(plan.active) === "true" || plan.active === true) || [];

  const uploadProps = {
    name: "receipt",
    multiple: false,
    maxCount: 1,
    accept: ".pdf,.png,.jpg,.jpeg",
    beforeUpload: (file: any) => {
      const isValid =
        file.type === "application/pdf" ||
        file.type === "image/png" ||
        file.type === "image/jpeg";
      if (!isValid) {
        message.error("Solo se permiten archivos PDF, PNG o JPG");
        return Upload.LIST_IGNORE;
      }
      if (file.size > 10 * 1024 * 1024) {
        message.error("El archivo no debe superar 10MB");
        return Upload.LIST_IGNORE;
      }
      setReceiptFile(file);
      return false; // Prevent auto upload
    },
    onRemove: () => {
      setReceiptFile(null);
    },
  };

  return (
    <Modal
      title="Asignar Plan a Empresa"
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      okText="Asignar"
      cancelText="Cancelar"
      destroyOnClose
    >
      <Form form={form} layout="vertical" name="assign_subscription_form">
        <Form.Item
          name="userId"
          label="Empresa"
          rules={[{ required: true, message: "Por favor seleccione una empresa" }]}
        >
          <Select
            showSearch
            placeholder="Seleccione la empresa"
            loading={loadingUsers}
            optionFilterProp="children"
            onSearch={(val) => setSearchTerm(val)}
            filterOption={false}
          >
            {usersData?.data?.map((user: IUser) => (
              <Select.Option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="planId"
          label="Plan de Suscripción"
          rules={[{ required: true, message: "Por favor seleccione un plan" }]}
        >
          <Select placeholder="Seleccione un plan" loading={loadingPlans}>
            {activePlans.map((plan) => (
              <Select.Option key={plan.id} value={plan.id}>
                {plan.name} - ${plan.price} / {plan.frequency}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="active"
          label="Estado inicial"
          valuePropName="checked"
          tooltip="Si está activo, la empresa tendrá acceso inmediatamente. Caso contrario, requerirá que el administrador apruebe o active la suscripción luego."
        >
          <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
        </Form.Item>

        <Form.Item
          label="Comprobante de Pago (PDF o Imagen)"
          tooltip="Suba un respaldo del comprobante de pago en formato PDF o imagen (PNG, JPG)"
        >
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Haz clic o arrastra el comprobante aquí
            </p>
            <p className="ant-upload-hint">
              Formatos aceptados: PDF, PNG, JPG. Máximo 10MB.
            </p>
          </Dragger>
        </Form.Item>

        <Form.Item name="notes" label="Notas Adicionales (Interno)">
          <Input.TextArea
            rows={3}
            placeholder="Razón por la cual se asignó manualmente este plan..."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AssignSubscriptionModal;
